import { useState, useEffect, useCallback } from 'react';
import DepositPayableComponent from './DepositPayable';
import { ErrorState } from 'types/Payable';
import {
  Deposit,
  DepositCondition,
  DepositDayType,
  DepositPayable,
  DepositType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { AgxRadioGroup, AgxSpacer } from '@urbanx/agx.ui-components2';
import { isEqual } from 'lodash-es';

const noBalanceDepositOption = 'noBalanceDeposit';
const yesBalanceDepositOption = 'yesBalanceDeposit';

const radioOptions = [
  {
    label: 'No balance deposit',
    value: noBalanceDepositOption,
  },
  {
    label: 'Balance deposit payable',
    value: yesBalanceDepositOption,
  },
];

interface BalanceDepositProps {
  id: string;
  onValueChanged: (balanceDeposit: Deposit) => void;
  defaultValue: Deposit;
  validate?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const BalanceDeposit = ({
  id,
  onValueChanged,
  defaultValue,
  validate,
  error,
  specialConditions,
  campaignId,
}: BalanceDepositProps) => {
  const [balanceDeposit, setBalanceDeposit] = useState<Deposit>({
    ...defaultValue,
    depositType: DepositType.Balance,
    isDepositSelected:
      defaultValue?.isDepositSelected ??
      (defaultValue?.amount || defaultValue?.percent ? true : false),
  });

  useEffect(() => {
    if (isEqual(defaultValue, balanceDeposit)) return;

    onValueChanged(balanceDeposit);
  }, [balanceDeposit]);

  const onRadioOptionChanged = useCallback((value?: string) => {
    if (value === noBalanceDepositOption) {
      setBalanceDeposit(prev => ({
        ...prev,
        condition: undefined,
        depositPayable: DepositPayable.NDaysFrom,
        dayType: DepositDayType.Days,
        isDepositSelected: false,
        amount: undefined,
        percent: undefined,
        daysFromDate: undefined,
        referenceDate: undefined,
      }));
    } else if (value === yesBalanceDepositOption) {
      setBalanceDeposit(prev => ({
        ...prev,
        isDepositSelected: true,
        condition: DepositCondition.NdaysFromDate,
      }));
    }
  }, []);

  const onDepositPayableChange = useCallback(
    ({ value }: { value: Deposit }) => {
      setBalanceDeposit(prev => ({
        ...prev,
        ...value,
      }));
    },
    []
  );

  return (
    <AgxSpacer direction="vertical" gap="xl" fill>
      <AgxRadioGroup
        id={'balanceDeposit-radioGroup'}
        label={`${DepositType.Balance} Deposit`}
        options={radioOptions}
        onChange={onRadioOptionChanged}
        value={
          balanceDeposit.isDepositSelected
            ? yesBalanceDepositOption
            : noBalanceDepositOption
        }
        subContent={
          balanceDeposit.isDepositSelected ? (
            <AgxSpacer direction="vertical" padding="md" fill>
              <DepositPayableComponent
                id={id}
                key={id}
                label={''}
                onValueChanged={onDepositPayableChange}
                defaultValue={balanceDeposit}
                validate={validate}
                error={error}
                required={true}
                campaignId={campaignId}
                specialConditions={specialConditions}
              />
            </AgxSpacer>
          ) : null
        }
        layout={'vertical'}
      />
    </AgxSpacer>
  );
};

export default BalanceDeposit;
